import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ChevronRight } from "lucide-react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import ProductCard from "@/components/ProductCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CategoryPage = () => {
  const { categoryId } = useParams();
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Scroll to top when category changes
    window.scrollTo(0, 0);
    
    const fetchData = async () => {
      setLoading(true);
      try {
        const [productsRes, categoriesRes] = await Promise.all([
          axios.get(`${API}/products?category=${categoryId}`),
          axios.get(`${API}/categories`)
        ]);
        setProducts(productsRes.data);
        const foundCategory = categoriesRes.data.find(c => c.id === categoryId);
        setCategory(foundCategory);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [categoryId]);

  const categoryName = category?.name || categoryId;
  const categoryImage = category?.image || "https://images.unsplash.com/photo-1612196808214-b8e1d6145a8c?w=800";

  return (
    <div className="min-h-screen bg-background" data-testid="category-page">
      <Header />

      {/* Category Hero */}
      <section className="relative h-64 md:h-80 overflow-hidden" data-testid="category-hero">
        <img
          src={categoryImage}
          alt={categoryName}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-foreground/50" />
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-6 md:px-12 w-full">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm text-background/80 mb-4">
              <Link to="/" className="hover:text-background transition-colors">Home</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-background">{categoryName}</span>
            </nav>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-background">
              {categoryName}
            </h1>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between mb-8">
            <p className="text-muted-foreground">
              {products.length} {products.length === 1 ? "product" : "products"} available
            </p>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-card rounded-2xl overflow-hidden">
                  <div className="aspect-square skeleton" />
                  <div className="p-5 space-y-3">
                    <div className="h-5 skeleton rounded w-3/4" />
                    <div className="h-4 skeleton rounded w-full" />
                    <div className="h-6 skeleton rounded w-1/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-muted-foreground text-lg">No products found in this category.</p>
              <Link to="/" className="text-primary font-medium mt-4 inline-block hover:underline">
                Browse all products
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {products.map((product, index) => (
                <div 
                  key={product.id} 
                  className="animate-fade-in-up"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <ProductCard product={product} />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CategoryPage;
